import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AnimalSymbol from "@/components/AnimalSymbol";
import { useAuth } from "@/contexts/AuthContext";
import { APP_NAME, SUBSCRIBE_URL } from "@/lib/config";

const FEATURES = [
  {
    eyebrow: "Structure",
    title: "Color-coded stick figures",
    body: "Mark each animal's ears, head, neck, front, back, rear, and tail by quality. Patterns appear visually across a pedigree.",
  },
  {
    eyebrow: "Lineage",
    title: "5-generation pedigrees",
    body: "Drag-and-drop ancestors. Wright's Coefficient of Inbreeding (COI) computed on the fly. Print to a single landscape page.",
  },
  {
    eyebrow: "Health",
    title: "Repeating-issue surfacing",
    body: "Track genetic, medical, and standard-compliance notes. The progeny analysis flags conditions repeating across multiple breeding partners.",
  },
  {
    eyebrow: "Records",
    title: "Photos, measurements & breedings",
    body: "Multi-photo galleries (drag-to-reorder), weight & height logs, litters tied to breedings, CSV import & export — your data, in your hands.",
  },
];

const SAMPLE_PARTS = {
  ears: "correct",
  head: "correct",
  neck: "improve",
  front: "correct",
  back: "correct",
  rear: "faulty",
  tail: "correct",
};

export default function WelcomePage() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--app-bg)]">
      {/* Top bar */}
      <header className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
        <div className="w-9 h-9 rounded-md bg-white border border-[var(--app-border)] flex items-center justify-center">
          <AnimalSymbol parts={{ ears: "correct", head: "correct", neck: "correct", front: "correct", back: "correct", rear: "correct", tail: "correct" }} size={28} />
        </div>
        <div className="font-display font-extrabold text-base tracking-tight">{APP_NAME}</div>
        <div className="flex-1" />
        <Link to="/login" className="text-sm text-[var(--app-text-soft)] hover:text-[var(--app-text)]" data-testid="welcome-signin">Sign in</Link>
        <Link to="/signup">
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="welcome-cta-top">Start free trial</Button>
        </Link>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-display font-semibold text-[var(--app-text-soft)] mb-4">
            For breeders of any four-legged species
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
            See your bloodline.<br />Then improve it.
          </h1>
          <p className="text-base text-[var(--app-text-soft)] mb-8 max-w-md leading-relaxed">
            {APP_NAME} is a working studbook for serious breeders. Track structure
            quality, health, and lineage across generations — then spot the
            traits that keep showing up before they become a problem.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="welcome-cta-hero">
                Start 30-day free trial
              </Button>
            </Link>
            <span className="text-xs text-[var(--app-text-soft)]">No credit card required.</span>
          </div>
        </div>

        {/* Visual */}
        <div className="bg-white border border-[var(--app-border)] rounded-lg p-8 flex items-center justify-center shadow-sm">
          <div className="text-center">
            <AnimalSymbol parts={SAMPLE_PARTS} size={220} />
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[var(--app-text-soft)] font-mono-tech">
              Sample · Rear: faulty · Neck: improve
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-[var(--app-border)]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.28em] font-display font-semibold text-blue-600">{f.eyebrow}</div>
              <div className="font-display font-extrabold text-xl tracking-tight">{f.title}</div>
              <div className="text-sm text-[var(--app-text-soft)] leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <div className="text-[10px] uppercase tracking-[0.28em] font-display font-semibold text-[var(--app-text-soft)] mb-3">
          Simple pricing
        </div>
        <div className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mb-3">
          <span className="font-mono-tech">$8</span> / month
        </div>
        <p className="text-sm text-[var(--app-text-soft)] mb-6 max-w-md mx-auto">
          One subscription, every feature. Cancel any time. The first 30 days are free
          — no card needed to start.
        </p>
        <Link to="/signup">
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="welcome-cta-pricing">
            Start your free trial
          </Button>
        </Link>
        <div className="mt-4 text-xs text-[var(--app-text-soft)]">
          Prefer to skip the trial?{" "}
          <a href={SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--app-text)] underline" data-testid="welcome-subscribe-direct">
            Subscribe directly
          </a>
        </div>
      </section>

      <footer className="border-t border-[var(--app-border)] py-6 text-center text-xs text-[var(--app-text-soft)]">
        © {new Date().getFullYear()} {APP_NAME}
      </footer>
    </div>
  );
}
